import { memo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { listContainer } from './styles';

import { ProfilePhotos } from '@/components/ProfileData/ProfilePhotos/ProfilePhotos.tsx';
import { UserDataFields } from '@/shared/type/userDataFields.ts';
import { ProfileBioBlock } from '@/components/ProfileData/Blocks/ProfileBioBlock.tsx';
import { InputContent } from '@/components/common/InputContent/InputContent.tsx';
import { HeightBlock } from '@/components/ProfileData/Blocks/HeightBlock.tsx';
import { CityBlock } from '@/components/ProfileData/Blocks/CityBlock.tsx';
import { LifestyleBlock } from '@/components/ProfileData/Blocks/LifestyleBlock.tsx';
import { PersonalBlock } from '@/components/ProfileData/Blocks/PersonalBlock.tsx';
import EditPopupOverlay from '@/components/Popup/EditProfile/EditPopupOverlay.tsx';
import EditBioPopup from '@/components/Popup/EditProfile/EditBioPopup.tsx';
import SelectCityPopup from '@/components/Popup/EditProfile/SelectCityPopup.tsx';
import EditHeightPopup from '@/components/Popup/EditProfile/EditHeightPopup.tsx';
import EditEmploymentPopup from '@/components/Popup/EditProfile/EditEmploymentPopup.tsx';
import EditBodyTypePopup from '@/components/Popup/EditProfile/EditBodyTypePopup.tsx';
import EditZodiacPopup from '@/components/Popup/EditProfile/EditZodiacPopup.tsx';
import EditSpokenLanguagesPopup from '@/components/Popup/EditProfile/EditSpokenLanguagesPopup.tsx';
import EditAlcoholPreferencePopup from '@/components/Popup/EditProfile/EditAlcoholPreferencePopup.tsx';
import EditSmokingStatusPopup from '@/components/Popup/EditProfile/EditSmokingStatusPopup.tsx';
import ProfileVideoBlock from '@/components/ProfileData/ProfileVideo/ProfileVideoBlock.tsx';
import { Gender } from '@/enums/apiEnums.ts';

interface EditProps {
  userDataFields: UserDataFields;
  updateFields: (fields: Partial<UserDataFields>) => void;
}

type PopupType =
  | 'bio'
  | 'city'
  | 'height'
  | 'employment'
  | 'bodyType'
  | 'zodiac'
  | 'spokenLanguages'
  | 'alcohol'
  | 'smoking'
  | null;

const Edit = ({ userDataFields, updateFields }: EditProps) => {
  const { t } = useTranslation();

  const [savingPhotoStates, setSavingPhotoStates] = useState<boolean[]>([
    false,
    false,
    false
  ]);
  const [isVideoUploading, setIsVideoUploading] = useState(false);

  const [activePopup, setActivePopup] = useState<PopupType>(null);

  const openPopup = (popup: PopupType) => setActivePopup(popup);
  const closePopup = () => setActivePopup(null);

  const isFemale = userDataFields.gender === Gender.FEMALE;

  return (
    <>
      <div style={listContainer}>
        <ProfilePhotos
          isEdit={true}
          photos={userDataFields.photos}
          updateFields={updateFields}
          isSaving={savingPhotoStates}
          setIsSaving={setSavingPhotoStates}
          isVideoUploading={isVideoUploading}
        />

        <InputContent style={{ marginTop: '24px' }}>
          <ProfileVideoBlock
            showBadge={isFemale}
            isEdit
            isAnyPhotoUploading={savingPhotoStates.some((s) => s)}
            onUploadingChange={setIsVideoUploading}
          />
        </InputContent>

        <InputContent title={t('bio_title')} style={{ marginTop: '24px' }}>
          <ProfileBioBlock onOpen={() => openPopup('bio')} />
        </InputContent>

        <InputContent
          title={t('city_title')}
          footer={t('city_select.notice')}
          style={{ marginTop: '28px' }}
        >
          <CityBlock onOpen={() => openPopup('city')} />
        </InputContent>

        <InputContent title={t('height_title')} style={{ marginTop: '28px' }}>
          <HeightBlock onOpen={() => openPopup('height')} />
        </InputContent>

        <InputContent
          title={t('personal_section')}
          style={{ marginTop: '28px' }}
        >
          <PersonalBlock
            onOpenEmployment={() => openPopup('employment')}
            onOpenBodyType={() => openPopup('bodyType')}
            onOpenZodiac={() => openPopup('zodiac')}
            onOpenSpokenLanguages={() => openPopup('spokenLanguages')}
          />
        </InputContent>

        <InputContent
          title={t('lifestyle_section')}
          style={{ marginTop: '28px' }}
        >
          <LifestyleBlock
            onOpenAlcohol={() => openPopup('alcohol')}
            onOpenSmoking={() => openPopup('smoking')}
          />
        </InputContent>
      </div>

      <EditPopupOverlay open={activePopup === 'bio'}>
        <EditBioPopup handleClose={closePopup} />
      </EditPopupOverlay>

      {/* City Popup */}
      <EditPopupOverlay open={activePopup === 'city'}>
        <SelectCityPopup handleClose={closePopup} />
      </EditPopupOverlay>

      {/* Height Popup */}
      <EditPopupOverlay open={activePopup === 'height'}>
        <EditHeightPopup handleClose={closePopup} />
      </EditPopupOverlay>

      {/* Employment Popup */}
      <EditPopupOverlay open={activePopup === 'employment'}>
        <EditEmploymentPopup handleClose={closePopup} />
      </EditPopupOverlay>

      {/* Body Type Popup */}
      <EditPopupOverlay open={activePopup === 'bodyType'}>
        <EditBodyTypePopup handleClose={closePopup} />
      </EditPopupOverlay>

      {/* Zodiac Popup */}
      <EditPopupOverlay open={activePopup === 'zodiac'}>
        <EditZodiacPopup handleClose={closePopup} />
      </EditPopupOverlay>

      {/* Spoken Languages Popup */}
      <EditPopupOverlay open={activePopup === 'spokenLanguages'}>
        <EditSpokenLanguagesPopup handleClose={closePopup} />
      </EditPopupOverlay>

      {/* Alcohol Preference Popup */}
      <EditPopupOverlay open={activePopup === 'alcohol'}>
        <EditAlcoholPreferencePopup handleClose={closePopup} />
      </EditPopupOverlay>

      {/* Smoking Status Popup */}
      <EditPopupOverlay open={activePopup === 'smoking'}>
        <EditSmokingStatusPopup handleClose={closePopup} />
      </EditPopupOverlay>
    </>
  );
};

export default memo(Edit);
